/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { CommonActions, FormActionPipelines, FormFlowState, whenFormInvalid } from '@gh/builder-shared';
import { ActionFlow, getInvalidFields } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { filter$, switchMap$ } from '@gh/rx/operators';
import { first, sortBy } from 'lodash';
import { ReportFyaDetailsBusinessService } from '../../resources/report-fya-details-business.service';
import {
  getReportFyaThresholdId,
  getReportFyaThresholdValueFieldOrder,
  ReportFyaDetails,
  ReportFyaDetailsTab,
  ReportFyaThreshold,
} from '../../resources/report-fya-details.model';
import { ReportFyaDetailsFormState } from './report-fya-details-form.service';

@Injectable()
export class ReportFyaDetailsCommandService {
  constructor(private form: ReportFyaDetailsFormState,
              private notifications: NotificationService,
              private pipelines: FormActionPipelines,
              private business: ReportFyaDetailsBusinessService,
              private dialogs: DialogService,
              private waitings: WaitingService,
              private commonActions: CommonActions) {
  }

  get completeFlow(): ActionFlow<FormFlowState<ReportFyaDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.execute(({ value }) => this.business.save(value)),
      finish: ({ value }) => {
        this.form.merge(value);
      },
    });
  }

  selectTab(tab: ReportFyaDetailsTab): void {
    this.form.setSelectedTab(tab);
  }

  setThresholdField(threshold: ReportFyaThreshold, field: string, value: any): void {
    this.form.setThresholdField(threshold, field, value);
  }

  resetFocusedCell(focusedCell?: FocusedCell): void {
    this.form.setFocusedCell(focusedCell);
  }

  setKeyGoal(officeId: ID, keyGoal: string): void {
    this.form.setKeyGoal(officeId, keyGoal);
  }

  setComments(officeId: ID, comments: string): void {
    this.form.setComments(officeId, comments);
  }

  reset(): void {
    this.dialogs
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.get().pipe(this.waitings.operator())))
      .subscribe((details) => {
        this.form.build(details);
      });
  }

  private handleErrors(): void {
    const { firstInvalidThreshold } = this.form;

    if (firstInvalidThreshold) {
      const firstInvalidField = first(sortBy(getInvalidFields(firstInvalidThreshold),
                                             getReportFyaThresholdValueFieldOrder));

      if (firstInvalidField) {
        this.notifications.error('msg_he_incorrect_data_in_tables');
        this.form.setSelectedTab(ReportFyaDetailsTab.IndexValues);
        this.form.setFocusedCell({
          id: getReportFyaThresholdId(firstInvalidThreshold),
          field: <string>firstInvalidField,
        });
      }
    }
  }
}
